import type { CSSProperties } from 'react';
import type { CutWindow } from './buildSceneLayout';
import type { TransitionDirection } from './types';

export interface CutFrameStyles {
  outgoing: CSSProperties | null;
  incoming: CSSProperties | null;
  overlay: { color: string; opacity: number } | null;
}

export function easeInOut(p: number): number {
  const c = Math.max(0, Math.min(1, p));
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

const r4 = (n: number) => Number(n.toFixed(4));

/** Direction = the way the INCOMING scene moves. 'left' means it enters from
 *  the right edge traveling leftward. */
const slideTransform = (dir: TransitionDirection, offPct: number): string => {
  switch (dir) {
    case 'left': return `translateX(${r4(offPct)}%)`;
    case 'right': return `translateX(${r4(-offPct)}%)`;
    case 'up': return `translateY(${r4(offPct)}%)`;
    case 'down': return `translateY(${r4(-offPct)}%)`;
  }
};

const wipeClip = (dir: TransitionDirection, remainingPct: number): string => {
  const v = r4(remainingPct);
  switch (dir) {
    case 'left': return `inset(0 0 0 ${v}%)`;   // reveal sweeps right -> left
    case 'right': return `inset(0 ${v}% 0 0)`;  // left -> right
    case 'up': return `inset(${v}% 0 0 0)`;     // bottom -> top
    case 'down': return `inset(0 0 ${v}% 0)`;   // top -> bottom
  }
};

const NONE: CutFrameStyles = { outgoing: null, incoming: null, overlay: null };

/** Transform-family invariant: these cuts have NO overlap, so exactly one
 *  scene is mounted at a time and nothing backs the frame — any style that
 *  moves or feathers content must also scale it up, or the empty composition
 *  shows through as a black flash at the cut.
 *
 *  With `scale(S)` the content overhangs the frame by (S-1)/2 on each side, so
 *  a translation of `t` (fraction of the frame) stays covered while
 *  t <= (S-1)/2. Ramping both on the same eased progress keeps that true at
 *  every instant: WHIP_TRANSLATE 0.30e vs margin 0.35e — a 5% safety band that
 *  never closes, and identity at e = 0 so the window edges don't jump. */
const WHIP_TRANSLATE_PCT = 30;
const WHIP_SCALE_BOOST = 0.7;
/** Blur samples beyond the element's edges, where there is nothing to sample —
 *  which feathers the frame border into darkness. A few percent of scale
 *  pushes that feathered edge outside the visible frame (at 1080px wide, 3%
 *  ≈ 32px of margin against a 14px blur's ~21px bleed). */
const BLUR_SCALE_BOOST = 0.06;

const panTransform = (axis: 'translateX' | 'translateY', pct: number, scale: number): string =>
  `${axis}(${r4(pct)}%) scale(${r4(scale)})`;

export function resolveTransitionStyles(cut: CutWindow, frame: number): CutFrameStyles | null {
  if (frame < cut.startFrame || frame >= cut.endFrame) return null;
  const span = cut.endFrame - cut.startFrame;
  if (span <= 0) return null;
  const p = (frame - cut.startFrame) / span;
  const dir = cut.transition.direction ?? 'left';
  const beforeBoundary = frame < cut.boundaryFrame;
  const spanA = cut.boundaryFrame - cut.startFrame;
  const spanB = cut.endFrame - cut.boundaryFrame;
  const pA = spanA > 0 ? (frame - cut.startFrame) / spanA : 1;
  const pB = spanB > 0 ? (frame - cut.boundaryFrame) / spanB : 1;

  switch (cut.transition.kind) {
    case 'crossfade':
      return { outgoing: null, incoming: { opacity: r4(easeInOut(p)) }, overlay: null };
    case 'slide':
      return { outgoing: null, incoming: { transform: slideTransform(dir, (1 - easeInOut(p)) * 100) }, overlay: null };
    case 'wipe':
      return { outgoing: null, incoming: { clipPath: wipeClip(dir, (1 - easeInOut(p)) * 100) }, overlay: null };
    case 'zoom-in':
      return beforeBoundary
        ? { outgoing: { transform: `scale(${r4(1 + 0.4 * easeInOut(pA))})` }, incoming: null, overlay: null }
        : NONE;
    case 'zoom-out':
      return beforeBoundary
        ? NONE
        : { outgoing: null, incoming: { transform: `scale(${r4(1 + 0.4 * (1 - easeInOut(pB)))})` }, overlay: null };
    case 'whip-pan': {
      const sign = dir === 'left' || dir === 'up' ? -1 : 1;
      const axis = dir === 'left' || dir === 'right' ? 'translateX' : 'translateY';
      // The pan rides on a matching zoom so the frame is never uncovered —
      // see WHIP_TRANSLATE_PCT. Panning alone used to sweep the only mounted
      // scene fully off-screen, flashing black at every cut.
      if (beforeBoundary) {
        const e = easeInOut(pA);
        return {
          outgoing: {
            transform: panTransform(axis, sign * e * WHIP_TRANSLATE_PCT, 1 + WHIP_SCALE_BOOST * e),
            filter: `blur(${r4(16 * e)}px)`,
          },
          incoming: null,
          overlay: null,
        };
      }
      const e = 1 - easeInOut(pB);
      return {
        outgoing: null,
        incoming: {
          transform: panTransform(axis, -sign * e * WHIP_TRANSLATE_PCT, 1 + WHIP_SCALE_BOOST * e),
          filter: `blur(${r4(16 * e)}px)`,
        },
        overlay: null,
      };
    }
    case 'blur': {
      // Scale with the blur so its feathered edge stays outside the frame.
      const e = beforeBoundary ? easeInOut(pA) : 1 - easeInOut(pB);
      const style: CSSProperties = {
        transform: `scale(${r4(1 + BLUR_SCALE_BOOST * e)})`,
        filter: `blur(${r4(14 * e)}px)`,
      };
      return beforeBoundary
        ? { outgoing: style, incoming: null, overlay: null }
        : { outgoing: null, incoming: style, overlay: null };
    }
    case 'dip-to-black':
    case 'dip-to-white':
      return {
        outgoing: null,
        incoming: null,
        overlay: {
          color: cut.transition.kind === 'dip-to-black' ? '#000' : '#fff',
          opacity: r4(beforeBoundary ? easeInOut(pA) : 1 - easeInOut(pB)),
        },
      };
    case 'camera-move':
      return NONE;
  }
}
